import { useState } from 'react'
import { useWasteData } from '../hooks/useWasteData.js'
import { formatMoney, formatQty } from '../lib/format.js'
import LifetimeSavings from './LifetimeSavings.jsx'
import OwnerAttestationModal from './OwnerAttestationModal.jsx'
import RunningTotal from './RunningTotal.jsx'
import SavingsBreakdownSheet from './SavingsBreakdownSheet.jsx'
import { Skeleton, SkeletonCard, SkeletonList } from './Skeleton.jsx'
import TopWastedToday from './TopWastedToday.jsx'
import WasteEntryForm from './WasteEntryForm.jsx'
import WastePatternsCard from './WastePatternsCard.jsx'
import Toast from './Toast.jsx'

// Home view for the Waste Tracking screen.
// Layout: lifetime-savings tile, running total, top-3, recent log
//         feed. Mark Waste sits inline near the top (Phase 2 fix —
//         the previous sticky-bottom CTA collided with the global
//         sticky AskAiBar that the InventoryHubScreen now mounts at
//         the same position; one of them had to move and Mark Waste
//         is the action paired with the section above it).
export default function WasteTrackingScreen() {
  const {
    items,
    settings,
    todayLogs,
    todayTotal,
    yesterdayTotal,
    top3,
    savingsSummary,
    savingsEvents,
    unconfirmedEvents,
    loading,
    refresh,
    deleteLog,
    recomputeSavings,
    confirmAllSavings,
    reviewSavingsEvents,
  } = useWasteData()

  const [formOpen, setFormOpen] = useState(false)
  const [attestOpen, setAttestOpen] = useState(false)
  const [breakdownOpen, setBreakdownOpen] = useState(false)
  const [toast, setToast] = useState('')
  const [deletingId, setDeletingId] = useState(null)

  async function handleAttestConfirm(decisions) {
    // Falls back to the bulk confirm-everything path if no decisions
    // were passed (defensive — modal always passes them now, but this
    // keeps confirmAllSavings reachable for any future caller).
    if (!decisions || Object.keys(decisions).length === 0) {
      const count = unconfirmedEvents?.length ?? 0
      const total = (unconfirmedEvents ?? []).reduce(
        (s, e) => s + Number(e.dollar_amount || 0), 0
      )
      await confirmAllSavings()
      setAttestOpen(false)
      setToast(`Verified ${formatMoney(total)} across ${count} ${count === 1 ? 'event' : 'events'}.`)
      return
    }

    const result = await reviewSavingsEvents(decisions)
    setAttestOpen(false)

    // Tailor the toast to what actually happened — owners deserve a
    // confirmation that reflects their edits and dismissals.
    const { confirmed, edited, dismissed, total } = result
    const verified = confirmed + edited
    const parts = []
    if (verified > 0) parts.push(`Verified ${formatMoney(total)} across ${verified} ${verified === 1 ? 'event' : 'events'}`)
    if (edited > 0)    parts.push(`${edited} edited`)
    if (dismissed > 0) parts.push(`${dismissed} dismissed`)
    setToast(parts.join(' · ') || 'Saved.')
  }

  async function handleLogged({ total }) {
    // Recompute today's savings so the LifetimeSavings tile updates
    // immediately. Without this, the tile reflects whatever the most
    // recent nightly cron run computed, which is stale after an insert.
    await recomputeSavings()
    // Refresh data so the new total is reflected when the toast shows.
    await refresh()
    setFormOpen(false)
    const newTodayTotal = Number(todayTotal) + Number(total)
    setToast(
      `Logged ${formatMoney(total)}. Today's total: ${formatMoney(newTodayTotal)}`
    )
  }

  // Remove an accidentally-logged entry. Confirms first so the manager
  // can't blow away a real entry by mis-tapping in a busy kitchen.
  // The hook's deleteLog also handles the savings recompute internally.
  async function handleDeleteLog(log) {
    if (deletingId) return
    const ok = window.confirm(
      `Remove this entry?\n\n${log.item_name} — ${formatMoney(log.total_cost)}\n\nToday's total will adjust accordingly.`
    )
    if (!ok) return
    setDeletingId(log.id)
    try {
      await deleteLog(log)
      setToast(
        `Removed ${formatMoney(log.total_cost)}. Today's total adjusted.`
      )
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[handleDeleteLog]', e)
      setToast('Could not remove entry. Try again.')
    } finally {
      setDeletingId(null)
    }
  }

  // First-paint skeleton — avoids the brief flicker of $0 totals
  // before Supabase queries land.
  if (loading && todayLogs.length === 0 && items.length === 0) {
    return (
      <div className="min-h-full bg-white pb-12">
        <main className="mx-auto max-w-screen-sm">
          <SkeletonCard />
          <SkeletonCard />
          <section className="mx-4 mt-6">
            <Skeleton className="h-4 w-32" />
            <SkeletonList rows={3} className="mt-2" />
          </section>
        </main>
      </div>
    )
  }

  return (
    <div className="min-h-full bg-white pb-12">
      {/* Header lives in App.jsx now (shared across tabs). */}
      <main className="mx-auto max-w-screen-sm">
        {/* Inline Mark Waste — primary action sits at the top so the
            manager doesn't have to scroll on a long log to find it,
            and so it doesn't collide with the InventoryHub's sticky
            AskAiBar. Mirrors Receive's "Log Delivery" semantically:
            primary action paired with the day's data. */}
        <section className="mx-4 mt-4" aria-label="Log waste">
          <button
            type="button"
            onClick={() => setFormOpen(true)}
            className="min-h-btn w-full rounded-lg bg-blue-600 px-4 py-4 text-2xl font-medium text-white shadow-sm hover:bg-blue-700 active:bg-blue-800"
          >
            Mark Waste
          </button>
        </section>

        <WastePatternsCard
          todayTotal={todayTotal}
          yesterdayTotal={yesterdayTotal}
          dailyTarget={settings.daily_target}
        />

        <LifetimeSavings
          summary={savingsSummary}
          dailyTarget={settings.daily_target}
          onReviewClick={() => setAttestOpen(true)}
          onBreakdownClick={() => setBreakdownOpen(true)}
        />

        <RunningTotal
          todayTotal={todayTotal}
          yesterdayTotal={yesterdayTotal}
          dailyTarget={settings.daily_target}
        />

        <TopWastedToday items={top3} />

        {/* Recent log feed */}
        <section className="mx-4 mt-6" aria-label="Recent waste entries">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-base font-medium">Recent entries</h2>
            <span className="text-xs text-black/60">
              {todayLogs.length} today
            </span>
          </div>

          {todayLogs.length === 0 ? (
            <div className="rounded-xl border border-dashed border-black/15 px-4 py-6 text-center text-sm text-black/60">
              No waste logged yet today. Tap{' '}
              <span className="font-medium text-blue-600">Mark Waste</span> to start.
            </div>
          ) : (
            <ul className="overflow-hidden rounded-xl border border-black/10">
              {todayLogs.slice(0, 8).map((l) => {
                const isDeleting = deletingId === l.id
                return (
                  <li
                    key={l.id}
                    className={`flex items-center justify-between gap-3 border-b border-black/5 px-4 py-3 last:border-b-0 ${
                      isDeleting ? 'opacity-50' : ''
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-base font-medium">
                        {l.item_name}
                      </div>
                      <div className="truncate text-xs text-black/60">
                        {formatQty(l.quantity, l.unit)} · {l.reason || 'No reason'}
                        {l.notes ? ` · ${l.notes}` : ''}
                      </div>
                    </div>
                    <div className="text-right text-base font-medium tabular-nums">
                      {formatMoney(l.total_cost)}
                    </div>
                    {/* Remove-an-entry button. Confirms before deleting. */}
                    <button
                      type="button"
                      onClick={() => handleDeleteLog(l)}
                      disabled={isDeleting}
                      aria-label={`Remove ${l.item_name} entry`}
                      title="Remove entry (logged by accident)"
                      className="min-h-btn min-w-[44px] rounded-lg px-2 text-base text-black/40 hover:bg-red-50 hover:text-red-600 active:bg-red-100 disabled:cursor-wait disabled:opacity-50"
                    >
                      {isDeleting ? '…' : '✕'}
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </section>
      </main>

      <WasteEntryForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onLogged={handleLogged}
        items={items}
        largeEntryThreshold={settings.large_entry_threshold}
      />

      <OwnerAttestationModal
        open={attestOpen}
        onClose={() => setAttestOpen(false)}
        onConfirmed={handleAttestConfirm}
        events={unconfirmedEvents}
      />

      <SavingsBreakdownSheet
        open={breakdownOpen}
        onClose={() => setBreakdownOpen(false)}
        events={savingsEvents}
        summary={savingsSummary}
        onReviewClick={() => setAttestOpen(true)}
      />

      <Toast message={toast} onDone={() => setToast('')} />
    </div>
  )
}
